<template>
  <div>
    <a-card style="margin: 20px" title="统计查询">
      <!-- 筛选区 -->
      <div class="filter-bar">
        <a-input
            v-model.trim="filterForm.merchantNo"
            allow-clear
            placeholder="商户协议编号"
            style="width: 200px; margin-right: 8px;"
        />
        <a-range-picker
            v-model="timeRange"
            style="width: 280px; margin-right: 8px;"
            value-format="YYYY-MM-DD"
        />
        <a-button type="primary" @click="onSearch">查询</a-button>
        <a-button style="margin-left: 8px;" @click="onReset">重置</a-button>
        <a-button
            v-if="$authority('payAgrStatExport')"
            icon="download"
            style="margin-left: 8px;"
            type="link"
            @click="onExport"
        >导出明细
        </a-button>
      </div>

      <!-- 概览 4 卡 -->
      <a-row :gutter="16" class="summary-row">
        <a-col :span="6">
          <a-card :loading="summaryLoading" size="small">
            <a-statistic title="总订单数" :value="summary.totalOrders || 0"/>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card :loading="summaryLoading" size="small">
            <a-statistic title="总金额（元）" :precision="2" :value="Number(summary.totalAmount || 0)"/>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card :loading="summaryLoading" size="small">
            <a-statistic :value="summary.successOrders || 0" :value-style="{color: '#52c41a'}" title="成功订单数"/>
          </a-card>
        </a-col>
        <a-col :span="6">
          <a-card :loading="summaryLoading" size="small">
            <a-statistic :value="summary.failOrders || 0" :value-style="{color: '#ff4d4f'}" title="失败订单数（服务费失败+支付失败）"/>
          </a-card>
        </a-col>
      </a-row>

      <!-- 商户订单分布图 -->
      <a-card size="small" style="margin-bottom: 16px;" title="商户订单分布（成功/失败）">
        <div ref="statChart" style="height: 300px; width: 100%;"/>
      </a-card>

      <!-- 明细表 -->
      <a-table
          :columns="detailColumns"
          :data-source="detailData"
          :loading="detailLoading"
          :pagination="false"
          :row-key="record => record.merchantNo"
          bordered
          size="small"
      >
        <template slot="totalAmount" slot-scope="text">
          <Balance :amount="text" prefix="￥"/>
        </template>
        <template slot="successRate" slot-scope="text">
          <span :style="{color: rateColor(text)}">{{ text }}</span>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-button type="link" size="small" @click="viewOrders(record)">查看订单</a-button>
        </template>
      </a-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <LcfsPagination :fn="getDetailData" :page-no.sync="pagination.pageNo" :page-size.sync="pagination.pageSize" :total="pagination.total"/>
      </div>
    </a-card>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import {message} from "ant-design-vue";
import LcfsPagination from "@/view/components/LcfsPagination.vue";
import Balance from "@/view/components/Balance.vue";

export default {
  name: 'PayAgrMerchantStat',
  components: {LcfsPagination, Balance},
  data() {
    return {
      filterForm: {merchantNo: ''},
      timeRange: [],
      summary: {},
      summaryLoading: false,
      detailData: [],
      detailLoading: false,
      pagination: {pageNo: 1, pageSize: 10, total: 0},
      chart: null,
      detailColumns: [
        {title: '商户协议编号', dataIndex: 'merchantNo', align: 'center'},
        {title: '商户协议名称', dataIndex: 'merchantName', align: 'center', customRender: (text) => text || '-'},
        {title: '总订单数', dataIndex: 'totalOrders', align: 'center', width: 100},
        {title: '总金额', dataIndex: 'totalAmount', align: 'center', width: 140, scopedSlots: {customRender: 'totalAmount'}},
        {title: '成功订单数', dataIndex: 'successOrders', align: 'center', width: 100},
        {title: '失败订单数', dataIndex: 'failOrders', align: 'center', width: 100},
        {title: '成功率', dataIndex: 'successRate', align: 'center', width: 100, scopedSlots: {customRender: 'successRate'}},
        {title: '操作', key: 'action', align: 'center', width: 100, scopedSlots: {customRender: 'action'}},
      ],
    };
  },
  mounted() {
    this.getSummary();
    this.getDetailData();
    window.addEventListener('resize', this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    buildParams() {
      const params = {merchantNo: this.filterForm.merchantNo || ''};
      if (this.timeRange && this.timeRange.length === 2) {
        params.createTimeStart = this.timeRange[0] + ' 00:00:00';
        params.createTimeEnd = this.timeRange[1] + ' 23:59:59';
      }
      return params;
    },

    onSearch() {
      this.pagination.pageNo = 1;
      this.getSummary();
      this.getDetailData();
    },

    onReset() {
      this.filterForm = {merchantNo: ''};
      this.timeRange = [];
      this.onSearch();
    },

    async getSummary() {
      this.summaryLoading = true;
      try {
        const res = await this.api.getPayAgrStatSummary(this.buildParams());
        if (res.code === 200) {
          this.summary = res.data || {};
        } else {
          message.warning(res.info || '获取统计概览失败');
        }
      } catch (error) {
        console.error('获取统计概览失败:', error);
      } finally {
        this.summaryLoading = false;
      }
    },

    async getDetailData() {
      this.detailLoading = true;
      try {
        const res = await this.api.getPayAgrStatDetail({
          ...this.buildParams(),
          pageNo: this.pagination.pageNo,
          pageSize: this.pagination.pageSize,
        });
        if (res.code === 200) {
          this.detailData = res.data.records || [];
          this.pagination.total = res.data.total || 0;
          this.$nextTick(this.renderChart);
        } else {
          message.warning(res.info || '获取统计明细失败');
        }
      } catch (error) {
        console.error('获取统计明细失败:', error);
      } finally {
        this.detailLoading = false;
      }
    },

    renderChart() {
      if (!this.$refs.statChart) return;
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.statChart);
      }
      const names = this.detailData.map(d => d.merchantNo);
      this.chart.setOption({
        tooltip: {trigger: 'axis', axisPointer: {type: 'shadow'}},
        legend: {data: ['成功订单', '失败订单']},
        grid: {left: 40, right: 20, bottom: 60, top: 40, containLabel: true},
        xAxis: {
          type: 'category',
          data: names,
          axisLabel: {interval: 0, rotate: names.length > 6 ? 30 : 0},
        },
        yAxis: {type: 'value', name: '订单数'},
        series: [
          {name: '成功订单', type: 'bar', stack: 'total', itemStyle: {color: '#52c41a'}, data: this.detailData.map(d => d.successOrders || 0)},
          {name: '失败订单', type: 'bar', stack: 'total', itemStyle: {color: '#ff4d4f'}, data: this.detailData.map(d => d.failOrders || 0)},
        ],
      }, true);
    },

    resizeChart() {
      if (this.chart) this.chart.resize();
    },

    rateColor(rate) {
      const num = parseFloat(rate);
      if (Number.isNaN(num)) return '#666';
      if (num >= 90) return '#52c41a';
      if (num >= 70) return '#faad14';
      return '#ff4d4f';
    },

    viewOrders(record) {
      this.$router.push({path: '/pay-agr/order', query: {merchantNo: record.merchantNo}});
    },

    async onExport() {
      const res = await this.api.exportPayAgrStat({
        ...this.buildParams(),
        pageNo: this.pagination.pageNo,
        pageSize: this.pagination.pageSize,
      });
      // 后端导出为 P2 预留接口，当前返回提示信息
      message.info(res.info || '导出功能开发中');
    },
  },
};
</script>

<style lang="less" scoped>
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 16px;
}

.summary-row {
  margin-bottom: 16px;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding: 12px 0;
}
</style>
